import {
    type DoneInvokeEvent,
    type InvokeCreator,
    Machine,
    type MachineOptions,
    assign,
    interpret,
    type ConditionPredicate,
    type ServiceConfig,
    type ActionFunctionMap,
} from "xstate";

export interface PaginationSchema {
    states: {
        initial: {};
        loading: {};
        loaded: {};
        error: {};
        lastPage: {};
    };
}

export interface PaginationContext {
    error: unknown;
    limit: number;
    offset: number;
}

export interface NextPageEvent {
    type: "nextPage";
}

export interface ResetEvent {
    type: "reset";
}

export type PaginationEvents = NextPageEvent | ResetEvent | DoneInvokeEvent<LoadResult>;

export interface LoadResult {
    isLastPage: boolean;
}

const PaginationGuards = {
    isLastPage: (_: PaginationContext, { data: { isLastPage } }: DoneInvokeEvent<LoadResult>) => isLastPage,
};

const PaginationActions = {
    setError: assign<PaginationContext>({
        error: (_: PaginationContext, event: DoneInvokeEvent<unknown>) => event.data,
    }),

    setFirstPage: assign<PaginationContext>({
        offset: 0,
        error: null,
    }),

    setLimit: assign<PaginationContext>({
        limit: ({ limit }) => (limit * 2 < 100 ? limit * 2 : 100),
    }),

    setNextPage: assign<PaginationContext>({
        offset: ({ offset, limit }) => offset + limit,
    }),
};

export const createPaginationService = (
    load: InvokeCreator<PaginationContext, PaginationEvents, LoadResult>,
    initalContext: Partial<PaginationContext> = {},
) =>
    interpret(
        createPaginationMachine(initalContext, {
            services: { loadNextPage: load } as Record<string, ServiceConfig<PaginationContext>>,
            guards: PaginationGuards as Record<string, ConditionPredicate<PaginationContext, PaginationEvents>>,
            actions: PaginationActions as ActionFunctionMap<PaginationContext, PaginationEvents>,
        }),
    );

export function createPaginationMachine(
    initalContext: Partial<PaginationContext>,
    options?: Partial<MachineOptions<PaginationContext, PaginationEvents>>,
) {
    return Machine<PaginationContext, PaginationSchema, PaginationEvents>(
        {
            id: "loadQuery",
            initial: "initial",
            context: {
                error: null,
                limit: 5,
                offset: 0,
                ...initalContext,
            },
            states: {
                initial: {
                    on: {
                        nextPage: {
                            target: "loading",
                        },
                    },
                },
                loading: {
                    invoke: {
                        id: "nextPageLoad",
                        src: "loadNextPage",
                        onDone: [
                            {
                                cond: "isLastPage",
                                target: "lastPage",
                            },
                            {
                                target: "loaded",
                            },
                        ],
                        onError: {
                            target: "error",
                            actions: ["setError"],
                        },
                    },
                },
                loaded: {
                    on: {
                        nextPage: {
                            target: "loading",
                            actions: ["setNextPage", "setLimit"],
                        },
                        reset: {
                            target: "initial",
                            actions: ["setFirstPage"],
                        },
                    },
                },
                error: {
                    on: {
                        reset: {
                            target: "initial",
                            actions: ["setFirstPage"],
                        },
                    },
                },
                lastPage: {
                    on: {
                        reset: {
                            target: "initial",
                            actions: ["setFirstPage"],
                        },
                    },
                },
            },
        },
        options,
    );
}
